import { useMemo } from "react";
import { ListingWithDetails, ListingNewWithDetailsDict } from "../../types";
import { ItemType } from "../../types";
import { ItemDetailView } from "../ItemDetailView";
import { TabsMap } from "../Tabs/types";
import { SearchableGrid } from "../page-layout/ListingGrid/SearchableGrid";
import { TabLayout } from "../Tabs/TabLayout";
import { typeIcons } from "../../config/ui-config";
import "./sidebar.css";

export type CategoryMap = { [key in ItemType]: string[] };
interface SidebarContentProps {
  // Tab state
  activeTab: ItemType;
  setActiveTab: any;

  // Data
  listingItemDict: ListingNewWithDetailsDict;
  categories: CategoryMap;

  // Handlers
  setShowJoinForm: () => void;
  setShowingOfferingForm: (offeringType: ItemType | null) => void;
  selectedItem: ListingWithDetails | null;
  setSelectedItem: (item: ListingWithDetails | null) => void;
  locations: any;
  loading?: boolean;
}

export function SidebarContent({
  activeTab,
  setActiveTab,
  categories,
  setShowJoinForm,
  setShowingOfferingForm,
  listingItemDict,
  selectedItem,
  setSelectedItem,
  loading = false,
}: SidebarContentProps) {
  const listingItems = useMemo(
    () => Object.values(listingItemDict),
    [listingItemDict]
  );

  const itemsByType = useMemo(() => {
    return groupListingItemsByType(listingItems);
  }, [listingItems]);

  const tabs: TabsMap = useMemo(() => {
    let ret: TabsMap = {} as TabsMap;
    const handleAddClick = () => {
      if (activeTab === "person") {
        setShowJoinForm();
        return;
      }
      setShowingOfferingForm(activeTab);
    };
    (Object.keys(itemsByType) as ItemType[]).forEach((itemType) => {
      ret[itemType] = {
        label: itemType.charAt(0).toUpperCase() + itemType.slice(1) + "s",
        icon: typeIcons[itemType],
        content: (
          <SearchableGrid
            listingItems={itemsByType[itemType]}
            onListingSelect={setSelectedItem}
            selectedListing={selectedItem}
            loading={loading}
            handleAddClick={handleAddClick}
            categories={categories[itemType]}
          />
        ),
      };
    });
    return ret;
  }, [itemsByType, selectedItem, setSelectedItem, loading]);

  // If there's a selected item, show detail view instead of tabs
  if (selectedItem) {
    return (
      <div className="sidebar-content">
        <ItemDetailView
          item={selectedItem}
          onConnectionClick={setSelectedItem}
        />
      </div>
    );
  }

  return (
    <div className="sidebar-content">
      <TabLayout tabs={tabs} />
    </div>
  );
}

function groupListingItemsByType(
  items: ListingWithDetails[]
): Record<ItemType, ListingWithDetails[]> {
  let ret: Record<ItemType, ListingWithDetails[]> = {} as Record<
    ItemType,
    ListingWithDetails[]
  >;
  for (let i = 0; i < items.length; i++) {
    const element = items[i];
    if (!ret[element.itemType]) {
      ret[element.itemType] = [];
    }
    ret[element.itemType].push(element);
  }
  return ret as Record<ItemType, ListingWithDetails[]>;
}
